import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px);
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  overflow-y: auto;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 200px); /* Even more bottom padding on mobile for multi-line buttons */
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${theme.spacing.lg};
  min-height: 0; /* Allow content to shrink */
`;

const Title = styled.h2`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h2};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
`;

const Description = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 0;
  
  /* Support for HTML tags - matching PacingMessage styling */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
`;

// Optimal Pace Display
const OptimalPaceCard = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary}20, ${theme.colors.secondary}20);
  border: 2px solid ${theme.colors.primary}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
`;

const OptimalHours = styled.div`
  font-family: ${theme.typography.headerFont};
  font-size: 1.5rem;
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.sm};
`;

const OptimalLabel = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: ${theme.spacing.lg};
`;



// Arousal Zone Visualization
const VisualizationContainer = styled.div`
  width: 80vw !important;
  max-width: 80vw !important;
  margin: 0 auto;
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.xl} ${theme.spacing.md}; /* Further reduce top padding */
  
  /* Override any parent constraints */
  min-width: 80vw !important;
  
  /* Ensure it's truly 80% of screen width on all devices */
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 80vw !important;
    max-width: 80vw !important;
    min-width: 80vw !important;
  }
`;

const VisualizationTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const ArousalScale = styled.div`
  position: relative;
  height: 60px;
  background: linear-gradient(to right, 
    ${theme.colors.success}40 0%, 
    ${theme.colors.primary}60 50%, 
    ${theme.colors.warning}40 100%);
  border-radius: ${theme.borderRadius.md};
  margin: 0;
  overflow: visible;
`;

const OptimalZone = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  background: ${theme.colors.primary}80;
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  left: ${props => props.left}%;
  width: ${props => props.width}%;
  
  &::after {
    content: 'Optimal Productivity Zone';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    font-family: ${theme.typography.bodyFont};
    font-size: ${theme.typography.sizes.small};
    font-weight: ${theme.typography.weights.medium};
    color: ${theme.colors.primary};
    background: white;
    padding: 4px 8px;
    border-radius: ${theme.borderRadius.md};
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    white-space: nowrap;
    border: 1px solid ${theme.colors.primary};
    z-index: 10;
    margin-top: 12px;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      font-size: ${theme.typography.sizes.xsmall};
      padding: 2px 6px;
    }
  }
`;

const CurrentArousalDot = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  background: ${theme.colors.accent};
  border: 3px solid white;
  border-radius: 50%;
  left: ${props => props.position}%;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  z-index: 20;
  
  &::after {
    content: 'Your Recent Pace';
    position: absolute;
    top: -42px;
    left: 50%;
    transform: translateX(-50%);
    font-family: ${theme.typography.bodyFont};
    font-size: ${theme.typography.sizes.small};
    font-weight: ${theme.typography.weights.medium};
    color: ${theme.colors.accent};
    background: white;
    padding: 4px 8px;
    border-radius: ${theme.borderRadius.md};
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    min-width: 120px;
    text-align: center;
    border: 1px solid ${theme.colors.accent};
    white-space: nowrap;
    
    @media (max-width: ${theme.breakpoints.tablet}) {
      font-size: ${theme.typography.sizes.xsmall};
      padding: 2px 6px;
      min-width: 100px;
      top: -37px;
    }
  }
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.textLight};
`;

// Legend/Key for the arousal zone visualization
const LegendContainer = styled.div`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.doryLighterBlue};
`;

const LegendTitle = styled.h4`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

const LegendItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  color: ${theme.colors.text};
`;

const OptimalZoneIndicator = styled.div`
  width: 24px;
  height: 16px;
  background: ${theme.colors.primary}80;
  border: 2px solid ${theme.colors.primary};
  border-radius: ${theme.borderRadius.sm};
  flex-shrink: 0;
`;

const CurrentLevelIndicator = styled.div`
  width: 16px;
  height: 16px;
  background: ${theme.colors.accent};
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  flex-shrink: 0;
`;

// Pacing Message
const PacingMessage = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: 0;
  
  /* Support for HTML tags - matching Description styling */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px;
  box-shadow: ${theme.shadows.sm};
  /* Button is now in content flow, adjust spacing */
  margin-top: 0;
  margin-bottom: 24px;
  /* Better width handling for longer text */
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  /* Multi-line text support for very long text */
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  /* Ensure proper touch targets on mobile */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }

  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    box-shadow: ${theme.shadows.sm};
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem;
    min-width: 150px;
  }
`;

const NoDataMessage = styled.div`
  background: ${theme.colors.neutral}20;
  border: 2px solid ${theme.colors.neutral}40;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  margin: ${theme.spacing.lg} 0;
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const NoDataTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const NoDataText = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  line-height: ${theme.typography.lineHeights.normal};
`;

const OptimalPaceScreen = ({ data, onNext, userProgress = {} }) => {
  const [optimalPaceData, setOptimalPaceData] = useState(userProgress.calculatedMetrics?.optimalPace);
  
  // Update optimal pace data when userProgress changes
  useEffect(() => {
    const newOptimalPaceData = userProgress.calculatedMetrics?.optimalPace;
    if (newOptimalPaceData) {
      console.log('OptimalPaceScreen: Received optimal pace data', newOptimalPaceData);
      setOptimalPaceData(newOptimalPaceData);
    }
  }, [userProgress]);
  
  // Process merge fields and format text content
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Check if we have optimal pace data
  if (!optimalPaceData) {
    return (
      <Container>
        <Content>
          {htmlContent && (
            <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
          )}
                    
          <NoDataMessage>
            <NoDataTitle>No Data Available</NoDataTitle>
            <NoDataText>
              Complete a journal entry with hours worked to see your optimal pace calculation.
            </NoDataText>
          </NoDataMessage>
          
          {/* CTA button appears right after content */}
          <ContinueButton onClick={() => onNext?.(data.key)}>
            {data.content?.ctaText || 'Continue'}
          </ContinueButton>
        </Content>
      </Container>
    );
  }

  const {
    currentArousal,
    pacingStatus,
    optimalArousalRange,
    hoursWorked,
    optimalHours,
    optimalHoursFormatted
  } = optimalPaceData;

  // Calculate positions for visualization (scale 0-10)
  const maxScale = 10;
  const optimalZoneLeft = (optimalArousalRange.min / maxScale) * 100;
  const optimalZoneWidth = ((optimalArousalRange.max - optimalArousalRange.min) / maxScale) * 100;
  const currentArousalPosition = (currentArousal / maxScale) * 100;

  // Generate pacing message based on status
  const getPacingMessage = () => {
    const formatHours = (hours) => {
      if (hours === 0) return 'zero hours';
      if (hours === 1) return 'one hour';
      if (hours === 2) return 'two hours';
      if (hours === 3) return 'three hours';
      if (hours === 4) return 'four hours';
      if (hours === 5) return 'five hours';
      if (hours === 6) return 'six hours';
      if (hours === 7) return 'seven hours';
      if (hours === 8) return 'eight hours';
      if (hours === 9) return 'nine hours';
      if (hours === 10) return 'ten hours';
      if (hours === 11) return 'eleven hours';
      if (hours === 12) return 'twelve hours';
      if (hours === 13) return 'thirteen hours';
      if (hours === 14) return 'fourteen hours';
      if (hours === 15) return 'fifteen hours';
      if (hours === 16) return 'sixteen hours';
      if (hours === 17) return 'seventeen hours';
      if (hours === 18) return 'eighteen hours';
      if (hours === 19) return 'nineteen hours';
      if (hours === 20) return 'twenty hours';
      if (hours === 21) return 'twenty-one hours';
      if (hours === 22) return 'twenty-two hours';
      if (hours === 23) return 'twenty-three hours';
      if (hours === 24) return 'twenty-four hours';
      
      // For hours beyond 24, use the written form
      const roundedHours = Math.round(hours);
      if (roundedHours <= 99) {
        const ones = roundedHours % 10;
        const tens = Math.floor(roundedHours / 10);
        
        let result = '';
        if (tens > 0) {
          if (tens === 2) result += 'twenty';
          else if (tens === 3) result += 'thirty';
          else if (tens === 4) result += 'forty';
          else if (tens === 5) result += 'fifty';
          else if (tens === 6) result += 'sixty';
          else if (tens === 7) result += 'seventy';
          else if (tens === 8) result += 'eighty';
          else if (tens === 9) result += 'ninety';
          
          if (ones > 0) result += '-';
        }
        
        if (ones > 0) {
          if (ones === 1) result += 'one';
          else if (ones === 2) result += 'two';
          else if (ones === 3) result += 'three';
          else if (ones === 4) result += 'four';
          else if (ones === 5) result += 'five';
          else if (ones === 6) result += 'six';
          else if (ones === 7) result += 'seven';
          else if (ones === 8) result += 'eight';
          else if (ones === 9) result += 'nine';
        }
        
        return `${result} hours`;
      }
      
      // Fallback for very large numbers
      return `${roundedHours} hours`;
    };

    const currentHours = formatHours(hoursWorked);

    switch (pacingStatus.status) {
      case 'over':
        return `You've been <strong>over-pacing</strong> recently. Reduce your pace from ${currentHours} to <strong>${optimalHoursFormatted}</strong> today to slide down into the Optimal Productivity Zone and achieve maximum productivity.`;
      case 'optimal':
        return `You've been working <strong>at your Optimal Pace</strong> recently which means you're achieving high productivity.</br></br>Adjusting your pace from ${currentHours} to <strong>${optimalHoursFormatted}</strong> today will shift you to the center of the Optimal Productivity Zone, allowing you to achieve <strong>maximum</strong> productivity.`;
      case 'under':
        return `You've been <strong>under-pacing</strong> recently. You have the capacity to increase your pace from ${currentHours} up to <strong>${optimalHoursFormatted}</strong> today which will slide you over into the Optimal Productivity Zone so you can achieve maximum productivity.`;
      default:
        return '';
    }
  };

  const pacingMessage = getPacingMessage();

  return (
    <Container>
      <Content>
        {htmlContent && (
          <Description dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {pacingMessage && (
          <PacingMessage dangerouslySetInnerHTML={{ __html: pacingMessage }} />
        )}
        
        <VisualizationContainer>
          <ArousalScale>
            <OptimalZone 
              left={optimalZoneLeft} 
              width={optimalZoneWidth}
            />
            <CurrentArousalDot 
              position={currentArousalPosition}
              value={currentArousal}
            />
          </ArousalScale>
        </VisualizationContainer>
        
        {/* CTA button appears right after content */}
        <ContinueButton onClick={() => onNext?.(data.key)}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default OptimalPaceScreen; 